import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@src/store/base-query';

export const fastingSessionsApi = createApi({
  reducerPath: 'fastingSessionsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['FastingSessions', 'FastingStats'],
  endpoints: (builder) => ({
    // Start a new fasting session
    startFastingSession: builder.mutation({
      query: (body) => ({
        url: 'fasting-sessions/start',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FastingSessions', 'FastingStats'],
    }),

    // End an ongoing fasting session
    endFastingSession: builder.mutation({
      query: ({ sessionId, ...body }) => ({
        url: `fasting-sessions/${sessionId}/end`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FastingSessions', 'FastingStats'],
    }),

    // Get current ongoing session
    getOngoingSession: builder.query({
      query: () => 'fasting-sessions/ongoing',
      providesTags: ['FastingSessions'],
    }),

    // Get fasting history
    getFastingHistory: builder.query({
      query: ({ limit = 20, skip = 0, startDate, endDate } = {}) => {
        const params = new URLSearchParams();
        if (limit) params.append('limit', limit);

        if (skip) params.append('skip', skip);

        if (startDate) params.append('startDate', startDate);

        if (endDate) params.append('endDate', endDate);

        return `fasting-sessions/history?${params.toString()}`;
      },
      providesTags: ['FastingSessions'],
    }),

    // Get fasting statistics
    getFastingStats: builder.query({
      query: () => 'fasting-sessions/stats',
      providesTags: ['FastingStats'],
    }),

    // Get calendar data for heatmap
    getFastingCalendar: builder.query({
      query: ({ startDate, endDate }) => `fasting-sessions/calendar?startDate=${startDate}&endDate=${endDate}`,
      providesTags: ['FastingSessions'],
    }),

    // Log a completed fasting session manually
    createManualFastingSession: builder.mutation({
      query: (body) => ({
        url: 'fasting-sessions/manual',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FastingSessions', 'FastingStats'],
    }),

    // Update an existing completed fasting session
    updateFastingSession: builder.mutation({
      query: ({ sessionId, ...body }) => ({
        url: `fasting-sessions/${sessionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FastingSessions', 'FastingStats'],
    }),

    // Delete a fasting session
    deleteFastingSession: builder.mutation({
      query: (sessionId) => ({
        url: `fasting-sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FastingSessions', 'FastingStats'],
    }),
  }),
});

export const {
  useStartFastingSessionMutation,
  useEndFastingSessionMutation,
  useGetOngoingSessionQuery,
  useGetFastingHistoryQuery,
  useGetFastingStatsQuery,
  useGetFastingCalendarQuery,
  useCreateManualFastingSessionMutation,
  useUpdateFastingSessionMutation,
  useDeleteFastingSessionMutation,
} = fastingSessionsApi;
